import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Skeleton,
  Tabs,
  Tab,
} from "@heroui/react";
import { cn } from "@/lib/utils";
import { useWorkspace, type Workspace } from "./WorkspaceContext";
import { useAuth } from "@/features/Auth";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizeCode } from "@/features/Workspaces/deriveWorkspaces";
import { useClientActions } from "@/hooks/useClientActions";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_WORKSPACE_GROUP_KEY,
  DEFAULT_WORKSPACE_GROUP_LABEL,
  groupLabelFromValue,
  normalizeKnownClientGroup,
  normalizeWorkspaceGroupKey,
} from "./clientGroups";
import {
  buildWorkspaceAdminPath,
  buildWorkspaceDashboardPath,
  normalizeWorkspaceGroupForPath,
} from "./workspaceRoutes";

const MODERN_PALETTE = [
  { bg: "bg-red-300", text: "text-red-900", border: "border-red-400" },
  { bg: "bg-orange-300", text: "text-orange-900", border: "border-orange-400" },
  { bg: "bg-amber-300", text: "text-amber-900", border: "border-amber-400" },
  { bg: "bg-yellow-300", text: "text-yellow-900", border: "border-yellow-400" },
  { bg: "bg-lime-300", text: "text-lime-900", border: "border-lime-400" },
  { bg: "bg-green-300", text: "text-green-900", border: "border-green-400" },
  {
    bg: "bg-emerald-300",
    text: "text-emerald-900",
    border: "border-emerald-400",
  },
  { bg: "bg-teal-300", text: "text-teal-900", border: "border-teal-400" },
  { bg: "bg-cyan-300", text: "text-cyan-900", border: "border-cyan-400" },
  { bg: "bg-sky-300", text: "text-sky-900", border: "border-sky-400" },
  { bg: "bg-blue-300", text: "text-blue-900", border: "border-blue-400" },
  {
    bg: "bg-indigo-300",
    text: "text-indigo-900",
    border: "border-indigo-400",
  },
  {
    bg: "bg-violet-300",
    text: "text-violet-900",
    border: "border-violet-400",
  },
  {
    bg: "bg-purple-300",
    text: "text-purple-900",
    border: "border-purple-400",
  },
  {
    bg: "bg-fuchsia-300",
    text: "text-fuchsia-900",
    border: "border-fuchsia-400",
  },
  { bg: "bg-pink-300", text: "text-pink-900", border: "border-pink-400" },
  { bg: "bg-rose-300", text: "text-rose-900", border: "border-rose-400" },
];

function hashToPalette(input: string): (typeof MODERN_PALETTE)[number] {
  let h = 0;
  for (let i = 0; i < input.length; i += 1)
    h = (h * 31 + input.charCodeAt(i)) >>> 0;
  return MODERN_PALETTE[h % MODERN_PALETTE.length];
}

function avatarClass(seed: string): string {
  const palette = hashToPalette(seed);
  return `${palette.bg} ${palette.text} ${palette.border}`;
}

function initialsFromName(name: string): string {
  const parts = name
    .replace(/[_/|-]+/g, " ")
    .split(" ")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 0) return "W";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  const a = parts[0][0] ?? "W";
  const b = parts[1][0] ?? "";
  return (a + b).toUpperCase();
}

function shouldHideTitleText(title: string, code: string | null): boolean {
  const titleKey = normalizeKey(title);
  const codeKey = normalizeKey(code);
  return titleKey.length > 0 && titleKey === codeKey;
}

function shortenWorkspaceBadge(code: string | null): string | null {
  if (!code) return null;
  const normalized = code.toLowerCase();
  if (normalized.includes("qtl")) return "QTL";
  if (normalized.startsWith("a7")) return "A7";
  if (normalized.startsWith("s1")) return "S1";
  const short = normalized.split(/[-_]/)[0] ?? normalized;
  if (short) return short.toUpperCase();
  return code.toUpperCase();
}

function fitAvatarTextClass(init: string): string {
  const n = init.length;
  if (n <= 2) return "text-[12px] tracking-tight";
  if (n <= 3) return "text-[11px] tracking-tight";
  return "text-[10px] tracking-tight";
}

function WorkspaceAvatar(props: {
  seed: string;
  name: string;
  showInitials?: boolean;
}) {
  const style = useMemo(() => avatarClass(props.seed), [props.seed]);
  const init = initialsFromName(props.name);

  return (
    <div
      className={cn(
        "grid h-7 w-7 place-items-center rounded-full border shadow-sm overflow-hidden select-none",
        style,
      )}
      aria-hidden="true"
    >
      {props.showInitials === false ? null : (
        <span
          className={cn(
            "px-1 text-center font-semibold leading-none",
            fitAvatarTextClass(init),
          )}
        >
          {init}
        </span>
      )}
    </div>
  );
}

function WorkspaceAvatarSkeleton() {
  return <Skeleton className="flex rounded-full w-9 h-9" />;
}

type NavItem = {
  id: string;
  module: "dashboard" | "admin";
  section: string;
  title: string;
  to: string;
  searchable: string;
  clientCode?: string | null;
  clientGroup?: string | null;
};

type TableEntry =
  | { label?: string | null; workspace_group?: string | null }
  | string;
type WorkbookEntry =
  | { label?: string | null; workspace_group?: string | null }
  | string;

function getTableLabel(tableEntry: TableEntry, fallback: string): string {
  if (typeof tableEntry === "string") return tableEntry;
  return (tableEntry?.label ?? fallback).toString();
}

function getTableClientGroup(tableEntry: TableEntry): string | null {
  if (typeof tableEntry === "string") return null;
  return tableEntry?.workspace_group ?? null;
}

function getWorkbookLabel(
  workbookEntry: WorkbookEntry,
  fallback: string,
): string {
  if (typeof workbookEntry === "string") return workbookEntry;
  return (workbookEntry?.label ?? fallback).toString();
}

function getWorkbookClientGroup(
  entry: WorkbookEntry | undefined,
): string | null {
  if (typeof entry === "string" || !entry) return null;
  return entry?.workspace_group ?? null;
}

type NormalizedGroup = {
  key: string;
  label: string;
};

function normalizeWorkspaceGroup(
  rawGroup: string | null | undefined,
): NormalizedGroup {
  const validated = normalizeKnownClientGroup(rawGroup);
  if (!validated) {
    return {
      key: DEFAULT_WORKSPACE_GROUP_KEY,
      label: DEFAULT_WORKSPACE_GROUP_LABEL,
    };
  }
  return { key: validated, label: groupLabelFromValue(validated) };
}

function fuzzyScore(queryRaw: string, candidateRaw: string): number {
  const q = queryRaw.trim().toLowerCase();
  const c = candidateRaw.trim().toLowerCase();
  if (!q) return 1;

  let qi = 0;
  let lastMatch = -1;
  let gaps = 0;
  let firstMatch = -1;

  for (let ci = 0; ci < c.length && qi < q.length; ci += 1) {
    if (c[ci] === q[qi]) {
      if (firstMatch === -1) firstMatch = ci;
      if (lastMatch !== -1) gaps += Math.max(0, ci - lastMatch - 1);
      lastMatch = ci;
      qi += 1;
    }
  }

  if (qi < q.length) return 0;

  const startBonus = 1 / (1 + (firstMatch === -1 ? 50 : firstMatch));
  const gapPenalty = 1 / (1 + gaps);
  const lengthPenalty = 1 / (1 + Math.max(0, c.length - q.length) / 18);

  return 0.45 + 0.3 * startBonus + 0.2 * gapPenalty + 0.05 * lengthPenalty;
}

function rankFuzzy<T>(
  query: string,
  items: T[],
  getText: (item: T) => string,
  limit: number,
): T[] {
  const q = query.trim();
  if (!q) return items.slice(0, limit);

  const scored: Array<{ item: T; score: number }> = [];
  for (const item of items) {
    const text = getText(item);
    const score = fuzzyScore(q, text);
    if (score > 0) scored.push({ item, score });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.item);
}

function rankFuzzyWithScore<T>(
  query: string,
  items: T[],
  getText: (item: T) => string,
  limit: number,
): Array<{ item: T; score: number }> {
  const q = query.trim();
  if (!q) return items.slice(0, limit).map((item) => ({ item, score: 1 }));

  const scored: Array<{ item: T; score: number }> = [];
  for (const item of items) {
    const text = getText(item);
    const score = fuzzyScore(q, text);
    if (score > 0) scored.push({ item, score });
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

function isQuantiliaWorkspace(code: string | null, label: string): boolean {
  const c = (code ?? "").toLowerCase();
  const l = label.toLowerCase();
  if (c.startsWith("qtl")) return true;
  if (l.startsWith("qtl")) return true;
  if (l.startsWith("quantilia")) return true;
  if (l.includes(" quantilia ")) return true;
  return false;
}

function normalizeKey(s: string | null | undefined): string {
  return (s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function splitNiceLabel(rawLabel: string): { main: string; suffix?: string } {
  const raw = rawLabel.trim();

  // Try to extract a short suffix like "A7 - FIA" -> main=A7, suffix=FIA
  // Also handles "A7_FIA", "A7/FIA", "A7: FIA"
  const m = raw.match(/^\s*([A-Za-z0-9]+)\s*[\-_:|·/]+\s*(.+?)\s*$/);
  if (!m) return { main: raw };

  const main = (m[1] ?? "").trim();
  const suffix = (m[2] ?? "").trim();
  if (!main) return { main: raw };
  if (!suffix) return { main };
  return { main, suffix };
}

function normalizeWorkspaceNameForDisplay(ws: Workspace): {
  title: string;
  meta?: string;
  isQtl: boolean;
} {
  const isQtl = isQuantiliaWorkspace(ws.code, ws.label);
  const code = (ws.code ?? "").toLowerCase();

  // Keep full code for A7-*/S1-INSTIT style to avoid split/breadcrumb display
  if (code.startsWith("a7-") || code.startsWith("a7_")) {
    return { title: (ws.code ?? ws.label).toUpperCase(), isQtl };
  }
  if (
    (code.startsWith("s1-") || code.startsWith("s1_")) &&
    code.includes("instit")
  ) {
    return { title: (ws.code ?? ws.label).toUpperCase(), isQtl };
  }

  // Remove noisy org prefixes for display (Quantilia + qtl- adds no value in UI)
  const cleaned = ws.label
    .trim()
    .replace(/^quantilia[\s-_:|·/]+/i, "")
    .replace(/^qtl[\s-_:|·/]+/i, "")
    .replace(/^qtl\b/i, "")
    .replace(/^quantilia\b/i, "")
    .trim();

  const label = cleaned.length > 0 ? cleaned : (ws.code ?? ws.label);
  const { main, suffix } = splitNiceLabel(label);

  // Avoid the "A5 A5" effect: if title and code are same, show only one
  const title = main.length > 0 ? main : (ws.code ?? ws.label);

  const titleKey = normalizeKey(title);
  const codeKey = normalizeKey(ws.code);

  // Meta rules (concise, non-repetitive):
  // - If code is redundant (A5), don't show code.
  // - If suffix exists (FIA/OPCVM), show it as the meta pill.
  // - Else if not redundant and it's Quantilia: still don't show code by default (already split by section).
  // - Else if not redundant and client: show code as meta (useful for debugging/search, but not always).
  if (suffix && normalizeKey(suffix) !== titleKey) {
    return { title, meta: suffix, isQtl };
  }

  if (titleKey === codeKey) {
    return { title, isQtl };
  }

  // If label is human and code is short, keep code as subtle meta for client items only
  if (!isQtl) {
    return { title, meta: ws.code ?? undefined, isQtl };
  }

  return { title, isQtl };
}

function SearchShell(props: {
  value: string;
  onChange: (v: string) => void;
  inputRef: React.Ref<HTMLInputElement>;
  placeholder: string;
  escLabel?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border bg-white px-4 py-3 transition",
        "border-slate-200",
        "focus-within:border-slate-300 focus-within:ring-2 focus-within:ring-slate-200",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="text-slate-400">
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>

        <input
          ref={props.inputRef}
          value={props.value}
          onChange={(event) => props.onChange(event.target.value)}
          placeholder={props.placeholder}
          className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
        />

        {props.escLabel ? (
          <div className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
            {props.escLabel}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function WorkspaceCard(props: {
  ws: Workspace;
  title: string;
  meta?: string;
  isActive: boolean;
  onPress: (event: React.MouseEvent<HTMLButtonElement>) => void;
  currentLabel: string;
}) {
  const hideTitle = shouldHideTitleText(props.title, props.ws.code);
  const metaText =
    props.meta && normalizeKey(props.meta) !== normalizeKey(props.title)
      ? props.meta
      : undefined;
  const badgeText =
    shortenWorkspaceBadge(props.ws.code ?? props.title) ?? props.title;
  const displayTitle = props.title;
  const displayMeta = metaText;

  return (
    <button
      type="button"
      onClick={props.onPress}
      className={cn(
        "w-full cursor-pointer text-left transition",
        "rounded-2xl bg-white",
        "px-3 py-2.5",
        "border border-slate-200",
        "hover:bg-slate-50 hover:border-slate-300",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300",
        props.isActive
          ? "border-slate-300 bg-slate-50/40 shadow-[0_1px_0_rgba(15,23,42,0.05)]"
          : "shadow-[0_1px_0_rgba(15,23,42,0.04)]",
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {hideTitle ? (
          <span
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold",
              avatarClass(props.ws.code ?? props.title),
            )}
            aria-hidden="true"
          >
            {badgeText}
          </span>
        ) : (
          <WorkspaceAvatar
            seed={props.ws.code ?? props.title}
            name={props.title}
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="min-w-0">
            <div className="truncate text-[12px] font-medium text-slate-900">
              {displayTitle}
              {displayMeta ? (
                <span className="text-slate-400"> &gt; {displayMeta}</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function SectionTitle(props: {
  children: React.ReactNode;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-3 px-1 pt-1 pb-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {props.children}
      </div>
      {props.right ? (
        <div className="text-[11px] text-slate-400">{props.right}</div>
      ) : null}
    </div>
  );
}

export default function WorkspaceCommandPalette({
  onWorkspaceSelected,
}: {
  onWorkspaceSelected?: (
    ws: Workspace,
    options?: { openInNewTab?: boolean },
  ) => void;
}) {
  const { current, available, setCurrent } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { actions: availableActions, hasActions } = useClientActions();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [showFirstBackdrop, setShowFirstBackdrop] = useState(false);
  const [query, setQuery] = useState("");
  const [tabKey, setTabKey] = useState<string>(DEFAULT_WORKSPACE_GROUP_KEY);

  const inputRef = useRef<HTMLInputElement>(null);
  const isNewTabIntent = (event: React.MouseEvent) =>
    event.metaKey || event.ctrlKey || event.button === 1;

  const sortedAvailable = useMemo(() => {
    const map = new Map<string, Workspace>();
    for (const ws of available) {
      const key = ws.code ?? ws.label;
      map.set(key, ws);
    }
    return [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        sensitivity: "base",
        numeric: true,
      }),
    );
  }, [available]);

  const userWorkbooks = user?.workbooks ?? {};
  const userTables = user?.tables ?? {};
  const sectionTranscription = user?.section_transcription ?? {};
  const sectionRouteOverrides = user?.section_route_overrides ?? {};

  const workspaceGroupKeysByCode = useMemo(() => {
    const map = new Map<string, Set<string>>();
    const addGroup = (
      code: string,
      rawGroup: string | null | undefined,
      options?: { fromTopLevel?: boolean },
    ) => {
      const groupKey = normalizeWorkspaceGroupKey(rawGroup, {
        // For entry-level values (workbook/table item), do not default to client
        // when workspace_group is missing; only explicit info should be applied.
        fallbackToDefault: false,
      });

      if (!groupKey) {
        // For top-level buckets, we still accept the default "client" fallback.
        if (options?.fromTopLevel) {
          const fallback = normalizeWorkspaceGroupKey(rawGroup, {
            fallbackToDefault: true,
          });
          if (!fallback) return;
          const set = map.get(code) ?? new Set<string>();
          set.add(fallback);
          map.set(code, set);
        }
        return;
      }

      const set = map.get(code) ?? new Set<string>();
      set.add(groupKey);
      map.set(code, set);
    };

    for (const [groupName, byWorkspace] of Object.entries(userWorkbooks)) {
      for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
        const code = normalizeCode(workspaceName);
        if (!code) continue;
        addGroup(code, groupName, { fromTopLevel: true });
        for (const entries of Object.values(byMenu)) {
          for (const entry of Object.values(entries)) {
            addGroup(code, getWorkbookClientGroup(entry));
          }
        }
      }
    }

    for (const [groupName, byMenu] of Object.entries(userTables)) {
      for (const [sectionName, tables] of Object.entries(byMenu)) {
        const code = normalizeCode(sectionName);
        if (!code) continue;
        addGroup(code, groupName, { fromTopLevel: true });
        for (const tableEntry of Object.values(tables)) {
          addGroup(code, getTableClientGroup(tableEntry));
        }
      }
    }

    return map;
  }, [userWorkbooks, userTables]);

  const isLoadingWorkspaces = available.length === 0;

  const tilesAll = useMemo(() => {
    return sortedAvailable.flatMap((ws) => {
      const { title, meta } = normalizeWorkspaceNameForDisplay(ws);
      const code = normalizeCode(ws.code ?? ws.label);
      const groupKeys = workspaceGroupKeysByCode.get(code);
      const resolvedGroupKeys =
        groupKeys && groupKeys.size > 0
          ? [...groupKeys]
          : [DEFAULT_WORKSPACE_GROUP_KEY];

      // Search should stay rich, UI should stay concise
      const searchable =
        `${ws.code ?? ""} ${ws.label} ${title} ${meta ?? ""}`.trim();

      return resolvedGroupKeys.map((groupKey) => {
        const group = normalizeWorkspaceGroup(
          groupKey === DEFAULT_WORKSPACE_GROUP_KEY ? null : groupKey,
        );
        return {
          ws,
          title,
          meta,
          searchable,
          groupKey: group.key,
          groupLabel: group.label,
        };
      });
    });
  }, [sortedAvailable, workspaceGroupKeysByCode]);

  const tilesRanked = useMemo(
    () => rankFuzzy(query, tilesAll, (t) => t.searchable, 900),
    [query, tilesAll],
  );

  const tilesByGroup = useMemo(() => {
    const map = new Map<string, typeof tilesRanked>();
    for (const tile of tilesRanked) {
      const list = map.get(tile.groupKey);
      if (list) {
        list.push(tile);
      } else {
        map.set(tile.groupKey, [tile]);
      }
    }
    return map;
  }, [tilesRanked]);

  const groupTabs = useMemo(() => {
    const map = new Map<
      string,
      { key: string; label: string; count: number }
    >();
    for (const tile of tilesRanked) {
      const existing = map.get(tile.groupKey);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(tile.groupKey, {
          key: tile.groupKey,
          label: tile.groupLabel,
          count: 1,
        });
      }
    }

    const preferredOrder: Record<string, number> = {
      client: 0,
      quantilia: 1,
      demo: 2,
      preprod: 3,
      poc: 4,
    };

    const tabs = [...map.values()].sort((a, b) => {
      const ia = preferredOrder[a.key];
      const ib = preferredOrder[b.key];
      const hasA = ia !== undefined;
      const hasB = ib !== undefined;
      if (hasA && hasB) return ia - ib;
      if (hasA) return -1;
      if (hasB) return 1;
      return a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
    });

    if (tabs.length === 0) {
      tabs.push({
        key: DEFAULT_WORKSPACE_GROUP_KEY,
        label: DEFAULT_WORKSPACE_GROUP_LABEL,
        count: 0,
      });
    }

    return tabs;
  }, [tilesRanked]);

  const menuItemsAll = useMemo(() => {
    const items: NavItem[] = [];

    for (const [groupName, byWorkspace] of Object.entries(userWorkbooks)) {
      for (const [workspaceName, byMenu] of Object.entries(byWorkspace)) {
        for (const [menuName, entries] of Object.entries(byMenu)) {
          for (const [uuid, workbookEntry] of Object.entries(entries)) {
            const section = menuName;
            const wsCode = workspaceName;
            const wsGroup =
              getWorkbookClientGroup(workbookEntry) ??
              normalizeKnownClientGroup(groupName);
            const group = normalizeWorkspaceGroupForPath(wsGroup ?? groupName);
            const to = buildWorkspaceDashboardPath({
              group,
              workspace: wsCode,
              uuid,
            });
            const title = getWorkbookLabel(workbookEntry, uuid);
            const searchable =
              `dashboard ${groupName} ${workspaceName} ${menuName} ${title} ${uuid} ${wsCode ?? ""} ${wsGroup ?? ""}`.trim();
            items.push({
              id: `dashboard:${uuid}`,
              module: "dashboard",
              section,
              title,
              to,
              searchable,
              clientCode: wsCode ?? null,
              clientGroup: wsGroup ?? null,
            });
          }
        }
      }
    }

    for (const [groupName, byMenu] of Object.entries(userTables)) {
      for (const [nameMenu, tables] of Object.entries(byMenu)) {
        const displayName = sectionTranscription[nameMenu] ?? nameMenu;
        const routeClient =
          sectionRouteOverrides[nameMenu] ?? nameMenu.toLowerCase();

        for (const [pathItem, tableEntry] of Object.entries(tables)) {
          const tableLabel = getTableLabel(tableEntry, pathItem);
          const tableClientGroup =
            getTableClientGroup(tableEntry) ??
            normalizeKnownClientGroup(groupName);
          const group = normalizeWorkspaceGroupForPath(
            tableClientGroup ?? groupName,
          );
          const to = buildWorkspaceAdminPath({
            group,
            workspace: nameMenu,
            path: `${routeClient}/${pathItem}`,
          });
          const searchable =
            `admin ${groupName} ${displayName} ${tableLabel} ${pathItem} ${routeClient} ${tableClientGroup ?? ""}`.trim();
          items.push({
            id: `admin:${groupName}:${nameMenu}:${pathItem}`,
            module: "admin",
            section: displayName,
            title: tableLabel,
            to,
            searchable,
            clientCode: routeClient,
            clientGroup: tableClientGroup ?? null,
          });
        }
      }
    }

    return items;
  }, [userWorkbooks, userTables, sectionTranscription, sectionRouteOverrides]);

  const mixedResults = useMemo(() => {
    if (!query.trim()) return [];
    const normalizedQuery = normalizeCode(query.trim());

    const wsScored = rankFuzzyWithScore(
      query,
      tilesAll,
      (t) => t.searchable,
      40,
    ).map((r) => ({
      kind: "workspace" as const,
      score: r.score,
      ws: r.item,
    }));

    const menuScored = rankFuzzyWithScore(
      query,
      menuItemsAll,
      (i) => i.searchable,
      40,
    ).map((r) => {
      const itemCode = normalizeCode(r.item.clientCode ?? "");
      const codeBoost =
        normalizedQuery && itemCode.startsWith(normalizedQuery) ? 0.6 : 0;
      const mismatchPenalty =
        normalizedQuery && itemCode && !itemCode.startsWith(normalizedQuery)
          ? -0.4
          : 0;
      return {
        kind: "menu" as const,
        score: r.score + codeBoost + mismatchPenalty,
        item: r.item,
      };
    });

    const wsBoosted = wsScored.map((r) => {
      const code = normalizeCode(r.ws.ws.code ?? "");
      const label = normalizeCode(r.ws.ws.label ?? "");
      const codeBoost =
        normalizedQuery &&
        (code.startsWith(normalizedQuery) || label.startsWith(normalizedQuery))
          ? 0.8
          : 0;
      const mismatchPenalty =
        normalizedQuery &&
        code &&
        !code.startsWith(normalizedQuery) &&
        !label.startsWith(normalizedQuery)
          ? -0.2
          : 0;
      return { ...r, score: r.score + codeBoost + mismatchPenalty };
    });

    return [...wsBoosted, ...menuScored]
      .sort((a, b) => b.score - a.score)
      .slice(0, 30);
  }, [query, tilesAll, menuItemsAll]);

  const isStartRoute = location.pathname === "/workspace-start";
  const isSingleWorkspace = available.length <= 1;
  const allowDismiss = isSingleWorkspace;

  const lastLocationRef = useRef<string>(
    `${location.pathname}${location.search}`,
  );
  const lastWorkspaceRef = useRef<string | null>(current?.code ?? null);

  useEffect(() => {
    const current = `${location.pathname}${location.search}`;
    if (current === lastLocationRef.current) return;
    lastLocationRef.current = current;
    if (open && !isStartRoute) {
      setShowFirstBackdrop(false);
      setOpen(false);
    }
  }, [location.pathname, location.search, open, isStartRoute]);

  useEffect(() => {
    const currentCode = current?.code ?? null;
    if (currentCode === lastWorkspaceRef.current) return;
    lastWorkspaceRef.current = currentCode;
    if (open && !isStartRoute) {
      setShowFirstBackdrop(false);
      setOpen(false);
    }
  }, [current?.code, open, isStartRoute]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isEditable =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;
      if (isEditable) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
      if (event.key === "Escape") {
        if ((allowDismiss || !showFirstBackdrop) && !isStartRoute)
          setOpen(false);
        return;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [allowDismiss, showFirstBackdrop, isStartRoute]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-command-palette", onOpen as EventListener);
    return () =>
      window.removeEventListener(
        "open-command-palette",
        onOpen as EventListener,
      );
  }, []);

  useEffect(() => {
    if (!open) {
      setShowFirstBackdrop(false);
      return;
    }

    const key = "workspace.commandPalette.firstOpen";
    let seen = false;
    try {
      seen = sessionStorage.getItem(key) === "1";
      if (!seen) sessionStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }

    if (!seen) setShowFirstBackdrop(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    if (groupTabs.some((tab) => tab.key === tabKey)) return;
    setTabKey(groupTabs[0]?.key ?? DEFAULT_WORKSPACE_GROUP_KEY);
  }, [groupTabs, tabKey]);

  const resultsCount = useMemo(() => {
    if (!query.trim()) return sortedAvailable.length;
    return tilesRanked.length;
  }, [query, sortedAvailable.length, tilesRanked.length]);

  const header = useMemo(() => {
    if (!current) return null;
    const { title, meta, isQtl } = normalizeWorkspaceNameForDisplay(current);
    return { title, meta, isQtl, code: current.code ?? null };
  }, [current]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setShowFirstBackdrop(false);
  };

  if (!open && !isStartRoute) return null;

  return (
    <Modal
      key={`${location.pathname}${location.search}`}
      isOpen={open}
      onOpenChange={handleOpenChange}
      size={isSingleWorkspace ? "2xl" : "4xl"}
      backdrop="blur"
      placement="center"
      scrollBehavior="inside"
      shouldBlockScroll
      isDismissable={(allowDismiss || !showFirstBackdrop) && !isStartRoute}
      hideCloseButton
      classNames={{
        backdrop:
          showFirstBackdrop || isStartRoute
            ? "backdrop-blur-md bg-white/80"
            : "backdrop-blur-md bg-black/5",
        wrapper: "items-center",
        body: "no-scrollbar",
      }}
    >
      <ModalContent
        className={cn(
          "rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden",
          isSingleWorkspace ? "max-w-lg" : "",
        )}
      >
        <ModalHeader
          className={cn(
            "px-8 pt-9 pb-7",
            isSingleWorkspace ? "px-6 pt-5 pb-5" : "",
          )}
        >
          {isSingleWorkspace ? (
            <div className="w-full px-1 py-1">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t("command_palette.actions", { defaultValue: "Actions" })}
                </div>
                {(showFirstBackdrop && !allowDismiss) || isStartRoute ? null : (
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Close"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {hasActions ? (
                <div className="mt-3 flex flex-wrap items-center gap-2.5">
                  {availableActions.map((action, index) => (
                    <div key={index}>{action.button}</div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="w-full flex flex-col gap-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {header && !isStartRoute ? (
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t("command_palette.current_workspace", {
                            defaultValue: "Workspace actuel",
                          })}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex max-w-full truncate rounded-full border px-3 py-1 text-[12px] font-semibold",
                              avatarClass(header.code ?? header.title),
                            )}
                          >
                            {header.title}
                          </span>
                          {header.meta ? (
                            <span className="truncate rounded-full bg-slate-900/5 px-2 py-1 text-[11px] text-slate-700">
                              {header.meta}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : isStartRoute ? (
                    <div className="flex items-center justify-center w-full py-2">
                      <img
                        alt="Quantilia"
                        className="h-10 w-auto"
                        src="/logo.png"
                      />
                    </div>
                  ) : null}
                </div>

                {(showFirstBackdrop && !allowDismiss) || isStartRoute ? null : (
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Close"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <SearchShell
                value={query}
                onChange={setQuery}
                inputRef={inputRef}
                placeholder={t("command_palette.search_placeholder", {
                  defaultValue: "Rechercher...",
                })}
                escLabel={
                  isStartRoute
                    ? undefined
                    : t("command_palette.esc", { defaultValue: "esc" })
                }
              />

              {hasActions ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50/40 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {t("command_palette.actions", { defaultValue: "Actions" })}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {availableActions.map((action, index) => (
                      <div key={index}>{action.button}</div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </ModalHeader>

        {isSingleWorkspace ? null : (
          <ModalBody className="px-8 pb-7 no-scrollbar">
            <div className="min-h-[520px] max-h-[62vh] overflow-auto pr-1 no-scrollbar">
              {query.trim() ? (
                <div>
                  <SectionTitle
                    right={
                      <span>
                        {t("command_palette.results_count", {
                          count: mixedResults.length,
                          defaultValue: "{{count}} résultats",
                        })}
                      </span>
                    }
                  >
                    {t("command_palette.results", {
                      defaultValue: "Résultats",
                    })}
                  </SectionTitle>
                  {mixedResults.length === 0 ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                      {t("command_palette.no_results", {
                        defaultValue: "Aucun résultat.",
                      })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {mixedResults.map((result, index) => {
                        if (result.kind === "workspace") {
                          const x = result.ws;
                          const hideTitle = shouldHideTitleText(
                            x.title,
                            x.ws.code,
                          );
                          const displayTitle = x.title;
                          return (
                            <button
                              key={`ws-${x.ws.code ?? x.ws.label}-${index}`}
                              type="button"
                              onClick={(event) => {
                                const openInNewTab = isNewTabIntent(event);
                                if (!openInNewTab) {
                                  setCurrent(
                                    { ...x.ws, group: x.groupKey },
                                    { source: "ui" },
                                  );
                                }
                                onWorkspaceSelected?.(
                                  { ...x.ws, group: x.groupKey },
                                  { openInNewTab },
                                );
                                if (!openInNewTab) {
                                  setShowFirstBackdrop(false);
                                  setOpen(false);
                                }
                              }}
                              className={cn(
                                "flex cursor-pointer items-center gap-3 rounded-2xl border px-3 py-2.5 text-left shadow-sm",
                                current?.code === x.ws.code
                                  ? "border-slate-300 bg-slate-50"
                                  : "border-slate-200 bg-white hover:bg-slate-50",
                              )}
                            >
                              <span
                                className={cn(
                                  "inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-semibold",
                                  avatarClass(x.ws.code ?? x.title),
                                )}
                              >
                                {shortenWorkspaceBadge(x.ws.code ?? x.title) ??
                                  x.title}
                              </span>
                              <div className="min-w-0 flex-1">
                                {hideTitle ? null : (
                                  <div className="truncate text-[12px] font-medium text-slate-900">
                                    {displayTitle}
                                  </div>
                                )}
                              </div>
                              <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600">
                                {t("command_palette.type_workspace", {
                                  defaultValue: "Workspace",
                                })}
                              </span>
                            </button>
                          );
                        }

                        const item = result.item;
                        const typeLabel =
                          item.module === "dashboard"
                            ? t("command_palette.type_dashboard", {
                                defaultValue: "Dashboard",
                              })
                            : t("command_palette.type_table", {
                                defaultValue: "Table",
                              });
                        return (
                          <button
                            key={`menu-${item.id}-${index}`}
                            type="button"
                            onClick={(event) => {
                              const openInNewTab = isNewTabIntent(event);
                              if (!openInNewTab && item.clientCode) {
                                const match = available.find(
                                  (w) =>
                                    normalizeCode(w.code) ===
                                    normalizeCode(item.clientCode ?? ""),
                                );
                                if (match) {
                                  const group = normalizeWorkspaceGroup(
                                    item.clientGroup,
                                  ).key;
                                  setCurrent(
                                    { ...match, group },
                                    { source: "ui" },
                                  );
                                }
                              }
                              if (openInNewTab) {
                                window.open(
                                  item.to,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              } else {
                                navigate(item.to);
                                setOpen(false);
                              }
                            }}
                            className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left text-xs shadow-sm hover:bg-slate-50"
                          >
                            <div className="min-w-0 flex items-center gap-2">
                              {item.clientCode ? (
                                <span
                                  className={cn(
                                    "inline-flex max-w-[64px] truncate rounded-xl border px-2 py-0.5 text-[11px] font-semibold",
                                    avatarClass(item.clientCode),
                                  )}
                                >
                                  {shortenWorkspaceBadge(item.clientCode)}
                                </span>
                              ) : null}
                              <div className="mt-0.5 truncate text-[12px] text-slate-900">
                                {item.section} &gt; {item.title}
                              </div>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-600">
                              {typeLabel}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <SectionTitle
                    right={
                      <span>
                        {t("command_palette.results_count", {
                          count: resultsCount,
                          defaultValue: "{{count}} résultats",
                        })}
                      </span>
                    }
                  >
                    {t("command_palette.workspaces", {
                      defaultValue: "Workspaces",
                    })}
                  </SectionTitle>

                  <Tabs
                    selectedKey={tabKey}
                    onSelectionChange={(k) => setTabKey(String(k))}
                    size="sm"
                    radius="full"
                    variant="light"
                    classNames={{
                      tabList:
                        "bg-slate-50 border border-slate-200 rounded-full p-1",
                      tab: "px-3 py-1.5 text-[12px]",
                      panel: "pt-3",
                    }}
                  >
                    {groupTabs.map((tab) => {
                      const groupTiles = tilesByGroup.get(tab.key) ?? [];
                      return (
                        <Tab
                          key={tab.key}
                          title={
                            <div className="flex items-center gap-2">
                              <span>{tab.label}</span>
                              <span className="text-[11px] text-slate-400">
                                {tab.count}
                              </span>
                            </div>
                          }
                        >
                          <div className="min-h-[360px]">
                            {isLoadingWorkspaces ? (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                {Array.from({ length: 12 }).map((_, i) => (
                                  <div
                                    key={i}
                                    className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm"
                                  >
                                    <div className="flex items-center gap-3">
                                      <WorkspaceAvatarSkeleton />
                                      <div className="w-full flex flex-col gap-2">
                                        <Skeleton className="h-3 w-2/3 rounded-lg" />
                                        <Skeleton className="h-3 w-1/2 rounded-lg" />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : groupTiles.length === 0 ? (
                              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                                {t("command_palette.no_results", {
                                  defaultValue: "Aucun résultat.",
                                })}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                {groupTiles.map((x) => (
                                  <WorkspaceCard
                                    key={x.ws.code ?? x.ws.label}
                                    ws={x.ws}
                                    title={x.title}
                                    meta={x.meta}
                                    isActive={current?.code === x.ws.code}
                                    onPress={(event) => {
                                      const openInNewTab =
                                        isNewTabIntent(event);
                                      if (!openInNewTab) {
                                        setCurrent(
                                          { ...x.ws, group: x.groupKey },
                                          { source: "ui" },
                                        );
                                      }
                                      onWorkspaceSelected?.(
                                        { ...x.ws, group: x.groupKey },
                                        { openInNewTab },
                                      );
                                      if (!openInNewTab) {
                                        setShowFirstBackdrop(false);
                                        setOpen(false);
                                      }
                                    }}
                                    currentLabel={t(
                                      "command_palette.current_badge",
                                      {
                                        defaultValue: "actuel",
                                      },
                                    )}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </Tab>
                      );
                    })}
                  </Tabs>
                </>
              )}
            </div>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  );
}
